// A push subscription and Tripmo's key (features/notifications.md, Gotchas): a browser subscribes with
// Tripmo's public key, and a subscription made with a key Tripmo has since replaced reaches nobody. So
// the page subscribes again with the key Tripmo has now, and tells Tripmo which subscription the new
// one replaces. Nothing here touches the page, so it's tested on its own (test/push-key.test.ts).

// What's used here of a browser's PushSubscription and PushManager (the tests stand in for them).
export interface Subscription {
  endpoint: string;
  options: { applicationServerKey: ArrayBuffer | null };
  unsubscribe(): Promise<boolean>;
}
export interface Manager<S extends Subscription> {
  getSubscription(): Promise<S | null>;
  subscribe(options: { userVisibleOnly: boolean; applicationServerKey: Uint8Array<ArrayBuffer> }): Promise<S>;
}

// This device's subscription, made with Tripmo's key, and the address of the one it replaced, if any.
export interface Renewed<S extends Subscription> {
  subscription: S;
  replaces: string | null;
}

// The subscription this browser has, made again with `key` if it was made with another; null when it
// has none. `ifUnknown`: also when the browser doesn't say which key it was made with (on a tap, where
// subscribing again costs nothing; never on its own, or a browser that never says would do it on every
// open).
export async function renew<S extends Subscription>(
  manager: Manager<S>,
  key: string,
  { ifUnknown = false } = {},
): Promise<Renewed<S> | null> {
  const existing = await manager.getSubscription();
  if (!existing) return null;
  const made = existing.options.applicationServerKey;
  if (made ? toBase64Url(made) === key : !ifUnknown) return { subscription: existing, replaces: null };
  // A browser keeps one subscription per worker: the old one goes first.
  await existing.unsubscribe();
  const subscription = await manager.subscribe({ userVisibleOnly: true, applicationServerKey: fromBase64Url(key) });
  return { subscription, replaces: subscription.endpoint === existing.endpoint ? null : existing.endpoint };
}

export function toBase64Url(buffer: ArrayBuffer): string {
  return btoa(String.fromCharCode(...new Uint8Array(buffer)))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

export function fromBase64Url(text: string): Uint8Array<ArrayBuffer> {
  const binary = atob(text.replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from(binary, (c) => c.charCodeAt(0));
}
